import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PencilIcon } from "@/components/developer/DashboardIcons";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

const Summary = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [summary, setSummary] = useState("");
  const { toast } = useToast();

  const { data: profile, isFetching } = useQuery({
    queryKey: ['developer-profile-summary'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('developer_profiles')
        .select('summary')
        .eq('user_id', user.user.id)
        .maybeSingle();

      if (error) throw error;
      setSummary(data?.summary);
      return data;
    }
  });

  const handleSave = async () => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('No user found');

      const { error } = await supabase
        .from('developer_profiles')
        .upsert({
          user_id: user.data.user.id,
          summary: summary,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' })
        .eq('user_id', user.data.user.id);

      if (error) throw error;

      setIsEditing(false);
      toast({
        title: "Success",
        description: "Summary updated successfully.",
      });
    } catch (error) {
      console.error('Error updating summary:', error);
      setSummary(profile?.summary || "");
      toast({
        title: "Error",
        description: "Failed to update summary. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setSummary(profile?.summary || "");
    setIsEditing(false);
  };

  if (isFetching) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Summary</h2>
        </div>
        <div className="animate-pulse">
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-xl font-semibold">Summary</h2>
        {!isEditing && (
          <Button variant="ghost" size="icon" onClick={() => {
            setSummary(profile?.summary || "");
            setIsEditing(true);
          }}>
            <PencilIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
      {isEditing ? (
        <div className="space-y-4">
          <Textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className="min-h-[120px] resize-none"
            placeholder="Write a brief summary about yourself..."
          />
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-gray-600">
          {summary || "No summary added yet. Click the edit button to add one."}
        </p>
      )}
    </Card>
  );
};

export default Summary;